// src/odoo/customers/components/CustomerContactsSection.tsx
// All comments are in English.

import React, { useEffect, useMemo, useState } from "react";
import type { CustomerContactsResponse } from "../api/overviewApi";
import { formatHKD } from "./utils";

interface Props {
  data?: CustomerContactsResponse;
  isLoading: boolean;
  error?: unknown;
}

type ContactSortField = "name" | "email" | "phone" | "order_count" | "gmv";

const PAGE_SIZE = 10;

// Odoo web base url for opening the customer form
const ODOO_WEB_BASE_URL = import.meta.env.VITE_ODOO_WEB_URL;

export const CustomerContactsSection: React.FC<Props> = ({
  data,
  isLoading,
  error,
}) => {
  if (isLoading) {
    return (
      <div className="rounded-lg border bg-card p-4 shadow-sm h-[220px] animate-pulse" />
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        Failed to load contacts.
      </div>
    );
  }

  if (!data) return null;

  const contacts = data.contacts;

  // Sorting state
  const [sortField, setSortField] = useState<ContactSortField>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  // Paging state
  const [page, setPage] = useState<number>(0);

  // Common normalizer for phone / email
  const normalizeField = (value: any): string | null => {
    if (!value) return null; // null / undefined / false / "" / 0
    if (typeof value === "number" && value === 0) return null;
    const s = String(value).trim();
    if (s === "" || s === "0") return null; // explicit "0" or empty string
    return s;
  };

  // Enrich contacts with display-ready email/phone
  type EnrichedContact = (typeof contacts)[number] & {
    _emailDisplay: string;
    _phoneDisplay: string;
  };

  const enrichedContacts: EnrichedContact[] = useMemo(
    () =>
      contacts.map((c) => {
        const email = normalizeField(c.email) || "-";
        const phone =
          normalizeField(c.mobile) || normalizeField(c.phone) || "-";

        return {
          ...c,
          _emailDisplay: email,
          _phoneDisplay: phone,
        };
      }),
    [contacts]
  );

  // Reset page when sort changes or data length changes
  useEffect(() => {
    setPage(0);
  }, [sortField, sortDir, enrichedContacts.length]);

  // Toggle sort helper
  const toggleSort = (field: ContactSortField) => {
    if (field === sortField) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  // Render sorting arrow icon
  const renderSortIcon = (field: ContactSortField) => {
    if (field !== sortField) {
      return <span className="opacity-40 ml-1">↕</span>;
    }
    return <span className="ml-1">{sortDir === "asc" ? "▲" : "▼"}</span>;
  };

  // Sorted contacts
  const sortedContacts = useMemo(() => {
    const list = [...enrichedContacts];

    list.sort((a, b) => {
      const dirFactor = sortDir === "asc" ? 1 : -1;

      switch (sortField) {
        case "name": {
          const s1 = a.name ?? "";
          const s2 = b.name ?? "";
          return s1.localeCompare(s2) * dirFactor;
        }
        case "email": {
          const s1 = a._emailDisplay ?? "";
          const s2 = b._emailDisplay ?? "";
          return s1.localeCompare(s2) * dirFactor;
        }
        case "phone": {
          const s1 = a._phoneDisplay ?? "";
          const s2 = b._phoneDisplay ?? "";
          return s1.localeCompare(s2) * dirFactor;
        }
        case "order_count": {
          const n1 = a.order_count ?? 0;
          const n2 = b.order_count ?? 0;
          if (n1 === n2) return 0;
          return n1 > n2 ? dirFactor : -dirFactor;
        }
        case "gmv": {
          const n1 = Number(a.gmv ?? 0);
          const n2 = Number(b.gmv ?? 0);
          if (n1 === n2) return 0;
          return n1 > n2 ? dirFactor : -dirFactor;
        }
        default:
          return 0;
      }
    });

    return list;
  }, [enrichedContacts, sortField, sortDir]);

  // Paging
  const totalItems = sortedContacts.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const startIndex = safePage * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, totalItems);
  const pagedContacts = sortedContacts.slice(startIndex, endIndex);

  const goPrevPage = () => {
    setPage((prev) => Math.max(0, prev - 1));
  };

  const goNextPage = () => {
    setPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm">
      <h2 className="text-sm font-semibold mb-2">Contacts under this company</h2>

      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-muted/60">
            <tr>
              <th
                className="px-2 py-1 text-left font-medium cursor-pointer select-none"
                onClick={() => toggleSort("name")}
              >
                <span className="inline-flex items-center">
                  Name
                  {renderSortIcon("name")}
                </span>
              </th>

              <th
                className="px-2 py-1 text-left font-medium cursor-pointer select-none"
                onClick={() => toggleSort("email")}
              >
                <span className="inline-flex items-center">
                  Email
                  {renderSortIcon("email")}
                </span>
              </th>

              <th
                className="px-2 py-1 text-left font-medium cursor-pointer select-none"
                onClick={() => toggleSort("phone")}
              >
                <span className="inline-flex items-center">
                  Phone
                  {renderSortIcon("phone")}
                </span>
              </th>

              {/* Country column removed */}

              <th
                className="px-2 py-1 text-right font-medium cursor-pointer select-none"
                onClick={() => toggleSort("order_count")}
              >
                <span className="inline-flex items-center justify-end w-full">
                  Orders (Overall)
                  {renderSortIcon("order_count")}
                </span>
              </th>

              <th
                className="px-2 py-1 text-right font-medium cursor-pointer select-none"
                onClick={() => toggleSort("gmv")}
              >
                <span className="inline-flex items-center justify-end w-full">
                  GMV (Overall)
                  {renderSortIcon("gmv")}
                </span>
              </th>
            </tr>
          </thead>

          <tbody>
            {pagedContacts.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-2 py-2 text-center text-muted-foreground"
                >
                  No contacts.
                </td>
              </tr>
            ) : (
              pagedContacts.map((c) => (
                <tr
                  key={c.customer_id}
                  className="border-b last:border-0 cursor-pointer hover:bg-gray-100 transition"
                  onClick={() => {
                    const url = `${ODOO_WEB_BASE_URL}#id=${c.customer_id}&cids=1-2-3&menu_id=306&action=484&model=res.partner&view_type=form`;
                    window.open(url, "_blank");
                  }}
                >
                  <td className="px-2 py-1 whitespace-nowrap">{c.name}</td>

                  <td className="px-2 py-1 whitespace-nowrap">
                    {c._emailDisplay}
                  </td>

                  <td className="px-2 py-1 whitespace-nowrap">
                    {c._phoneDisplay}
                  </td>

                  <td className="px-2 py-1 text-right">
                    {c.order_count.toLocaleString("en-HK")}
                  </td>

                  <td className="px-2 py-1 text-right">
                    {formatHKD(c.gmv)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pager */}
      {totalItems > 0 && (
        <div className="flex items-center justify-between mt-2 text-[11px] text-muted-foreground">
          <div>
            Showing{" "}
            <span className="font-medium">
              {startIndex + 1}-{endIndex}
            </span>{" "}
            of <span className="font-medium">{totalItems}</span> contacts
          </div>

          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              className="px-2 py-1 border rounded disabled:opacity-40"
              onClick={goPrevPage}
              disabled={safePage <= 0}
            >
              Prev
            </button>
            <span>
              Page{" "}
              <span className="font-medium">{safePage + 1}</span> /{" "}
              <span className="font-medium">{totalPages}</span>
            </span>
            <button
              type="button"
              className="px-2 py-1 border rounded disabled:opacity-40"
              onClick={goNextPage}
              disabled={safePage >= totalPages - 1}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
